import type { SortingFn } from "@tanstack/react-table";

import { getDateFormat } from "~/utils/Helper";
import { Org } from "~/utils/Types";
import dayjs from "~/utils/dayjs";

import type { MeqRowData } from "./types";

/**
 * Client-side sorting logic ported from the old (now-deleted) MRT-based
 * wrapper. Pure comparators (`compareDateOnly`,
 * `compareDatetime`, `compareNumbers`) are exported directly so they're unit
 * testable without constructing TanStack `Row` objects; `buildSortingFns`
 * wraps them into the row-based registry `useMeqTableInstance.ts` passes to
 * the engine's `sortingFns` option.
 *
 * This module is internal to the MeqTable engine seam (like
 * useMeqTableInstance.ts, it may reference `@tanstack/react-table` types) —
 * it is NOT part of the public call-site-facing surface (that's types.ts).
 */

/**
 * Natural alpha-numeric split — the same regex TanStack's own built-in
 * `alphanumeric`/`alphanumericCaseSensitive` sortingFns use internally
 * (@tanstack/table-core's sortingFns.ts) to compare mixed strings like
 * "Row2" before "Row10" numerically instead of lexically. Ported here
 * (from the old wrapper's export of the same name) so migrated call sites
 * depending on `reSplitAlphaNumeric` have a MeqTable-owned equivalent.
 * Natural alpha-numeric sorting itself is free via `sortingFn:
 * "alphanumeric"` — a `MeqBuiltInSortingFnName` the engine resolves without
 * any custom registration.
 */
export const reSplitAlphaNumeric = /([0-9]+)/gm;

/**
 * Parse a formatted cell value. `buildSortingFns` lowercases cell values
 * ("01 jun 2026"), which Moment's case-insensitive parser accepted but
 * dayjs's customParseFormat rejects (month-name lookup is case-sensitive) —
 * fall back to the default parser, which handles lowercased month names,
 * preserving the old sort behavior.
 */
const parseFormattedDate = (value: string, format: string) => {
  const strict = dayjs(value, format);
  return strict.isValid() ? strict : dayjs(value);
};

const compareFormattedDates = (
  aStr: string,
  bStr: string,
  org: Org,
  formatType: "short" | "default",
): number => {
  if (aStr === null || aStr === undefined) {
    return -1;
  }
  if (bStr === null || bStr === undefined) {
    return 1;
  }
  const dateA = parseFormattedDate(aStr, getDateFormat(org, formatType));
  const dateB = parseFormattedDate(bStr, getDateFormat(org, formatType));
  return dateA > dateB ? 1 : dateA < dateB ? -1 : 0;
};

/** Ported from the old wrapper's `compareDateOnly`. */
export const compareDateOnly = (aStr: string, bStr: string, org: Org): number =>
  compareFormattedDates(aStr, bStr, org, "short");

/** Ported from the old wrapper's `compareDatetime`. */
export const compareDatetime = (aStr: string, bStr: string, org: Org): number =>
  compareFormattedDates(aStr, bStr, org, "default");

const isNumberLike = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  !isNaN(Number(value));

/**
 * Numeric-aware comparator for number columns whose values may arrive as
 * strings (mirrors the number-range filter's own numeric coercion). Blank
 * or non-numeric values sort first, matching dateOnly/datetime's
 * null-first behavior.
 */
export const compareNumbers = (a: unknown, b: unknown): number => {
  const aIsNumber = isNumberLike(a);
  const bIsNumber = isNumberLike(b);
  if (!aIsNumber && !bIsNumber) {
    return 0;
  }
  if (!aIsNumber) {
    return -1;
  }
  if (!bIsNumber) {
    return 1;
  }
  const numA = Number(a);
  const numB = Number(b);
  return numA > numB ? 1 : numA < numB ? -1 : 0;
};

/**
 * Rank of one ordinal grade. A grade is a whole number optionally carrying a
 * `+` suffix marking the open-ended top of its band ("10+"), so `10+` must
 * outrank `10` while `2` still ranks below `10` — which neither a numeric nor
 * a lexical comparison gets right on its own. Doubling leaves room for the
 * suffix without floats.
 */
export const toGradeRank = (value: unknown): number => {
  const match = /^(\d+)(\+?)$/.exec(String(value ?? "").trim());
  return match === null
    ? Number.NEGATIVE_INFINITY
    : Number(match[1]) * 2 + (match[2] === "+" ? 1 : 0);
};

/**
 * Comparator for ordinal grade columns. Blank or unparseable values sort
 * first, matching dateOnly/datetime/number's null-first behavior.
 */
export const compareGrades = (a: unknown, b: unknown): number => {
  const rankA = toGradeRank(a);
  const rankB = toGradeRank(b);
  return rankA === rankB ? 0 : rankA > rankB ? 1 : -1;
};

/**
 * Row-based sorting fns registered under the engine's `sortingFns` option
 * (see useMeqTableInstance.ts). Named to match `MeqCustomSortingFnName` so
 * column defs can reference them as `sortingFn: "dateOnly" | "datetime" |
 * "grade" | "number"`.
 */
export const buildSortingFns = <TData extends MeqRowData>(
  org: Org,
): Record<string, SortingFn<TData>> => ({
  dateOnly: (rowA, rowB, columnId) =>
    compareDateOnly(
      String(rowA.getValue(columnId) ?? "").toLowerCase(),
      String(rowB.getValue(columnId) ?? "").toLowerCase(),
      org,
    ),
  datetime: (rowA, rowB, columnId) =>
    compareDatetime(
      String(rowA.getValue(columnId) ?? "").toLowerCase(),
      String(rowB.getValue(columnId) ?? "").toLowerCase(),
      org,
    ),
  grade: (rowA, rowB, columnId) =>
    compareGrades(rowA.getValue(columnId), rowB.getValue(columnId)),
  number: (rowA, rowB, columnId) =>
    compareNumbers(rowA.getValue(columnId), rowB.getValue(columnId)),
});
